import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { useUserContext } from "../context/UserContext";
import { linkDiscord, isDiscordLinked } from "../services/DiscordService";
import { toast } from "react-toastify";
import { Loader2, AlertTriangle, X } from "lucide-react";
import { isFreeTier } from "../utils/userUtils";

interface EmptyStateProps {
  title?: string;
  message?: string;
  className?: string;
  icon?: React.ReactNode;
  showDiscord?: boolean; // New prop to show Discord button/link
  align?: "center" | "top";
  variant?: "default" | "panel";
  tipsTitle?: string;
  tips?: string[];
  actionButtons?: {
    text: string;
    to?: string;
    onClick?: () => void;
    icon?: React.ReactNode;
    variant?: "primary" | "secondary";
  }[];
  actionButton?: {
    text: string;
    to: string;
    icon?: React.ReactNode;
  };
}

const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  message,
  className = "",
  showDiscord = false,
  align = "center",
  variant = "default",
  tipsTitle,
  tips,
  actionButtons,
  actionButton,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const isFreeUser = isFreeTier(user);
  const isTop = align === "top";
  const isPanel = variant === "panel";
  const actions =
    actionButtons && actionButtons.length
      ? actionButtons
      : actionButton
      ? [{ ...actionButton, variant: "primary" as const }]
      : [];

  // Discord modal state
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [isLinkingDiscord, setIsLinkingDiscord] = useState(false);

  // Discord SVG Icon Component
  const DiscordIcon = ({
    size = 18,
    className = "",
  }: {
    size?: number;
    className?: string;
  }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z" />
    </svg>
  );

  const handleLinkDiscord = async () => {
    if (!user) return;

    setIsLinkingDiscord(true);
    try {
      const response = await linkDiscord();
      // Redirect to Discord OAuth
      window.location.href = response.redirectUrl;
    } catch (error) {
      console.error("Error linking Discord:", error);
      toast.error(t("settings.discordLinkError"));
      setIsLinkingDiscord(false);
    }
  };

  const handleDiscordAction = () => {
    const discordLinked = isDiscordLinked(user);
    if (discordLinked) {
      // Open Discord in a new tab
      window.open("https://discord.gg/3DTMngFYb9", "_blank");
    } else {
      // Show modal to link Discord
      setShowDiscordModal(true);
    }
  };

  return (
    <>
      <div
        className={`flex flex-col h-full px-4 ${
          isTop
            ? "py-0 items-start justify-start text-left"
            : "py-12 min-h-64 items-center justify-center text-center"
        } relative ${className}`}
      >
        {/* Simple title and message */}
        <div
          className={`${
            isPanel
              ? "w-full max-w-2xl rounded-xl border border-gray-200/50 dark:border-dark-700/50 bg-white/80 dark:bg-dark-900/70 shadow-sm backdrop-blur-sm p-4"
              : `${isTop ? "max-w-2xl" : "max-w-sm"} px-2`
          }`}
        >
          <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-100 leading-tight">
            {title || t("common.noDataFound")}
          </h3>
          <p className="text-gray-600 dark:text-dark-300 leading-snug text-[13px] mt-1.5">
            {message || t("common.tryAdjustingFilters")}
          </p>

          {tips && tips.length > 0 && (
            <div className="pt-3 border-t border-gray-200/50 dark:border-dark-700/50 mt-3">
              {tipsTitle && (
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-dark-400 font-semibold mb-2">
                  {tipsTitle}
                </p>
              )}
              <ul className="space-y-1.5">
                {tips.map((tip) => (
                  <li
                    key={tip}
                    className="flex items-start gap-2 text-[13px] text-gray-600 dark:text-dark-300"
                  >
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-primary-500/80 flex-shrink-0"></span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          {actions.length > 0 && (
            <div
              className={`mt-3 flex flex-wrap gap-2 ${
                isTop ? "justify-start" : "justify-center"
              }`}
            >
              {actions.map((action) => {
                const variantClass =
                  action.variant === "secondary"
                    ? "border border-gray-200 dark:border-dark-600 text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-800"
                    : "bg-primary-600 text-white hover:bg-primary-700";
                const actionClass = `inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-medium transition-all shadow-sm hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/60 ${variantClass}`;

                if (action.to) {
                  return (
                    <Link key={action.text} to={action.to} className={actionClass}>
                      {action.icon}
                      {action.text}
                    </Link>
                  );
                }

                return (
                  <button
                    key={action.text}
                    onPointerDown={action.onClick}
                    className={actionClass}
                  >
                    {action.icon}
                    {action.text}
                  </button>
                );
              })}
            </div>
          )}

          {/* Animated status indicator */}
          {showDiscord && (
            <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-dark-700/50 space-y-2">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200 rounded-full text-xs font-medium">
                <div className="relative">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-1.5 h-1.5 bg-green-400 rounded-full animate-ping opacity-50"></div>
                </div>
                {t("products.waitingPeriod")}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-dark-400">
                {t("products.supportContact")}
              </p>
            </div>
          )}
        </div>

        {/* Enhanced Discord section */}
        {showDiscord && user && !isFreeUser && (
          <div className="mt-4 w-full max-w-2xl animate-fade-in">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/60 dark:from-blue-950/40 dark:to-indigo-950/40 dark:border-blue-400/20 rounded-lg p-3 shadow-sm hover:shadow transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded flex items-center justify-center">
                  <DiscordIcon size={14} className="text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-800 dark:text-dark-100">
                  Discord Community
                </span>
              </div>

              <p className="text-[13px] text-blue-700 dark:text-blue-200/90 mb-2 leading-snug">
                {t("products.discordSuggestion")}
              </p>

              <button
                onPointerDown={handleDiscordAction}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 text-xs font-medium shadow-sm hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/60"
              >
                <DiscordIcon size={14} />
                {isDiscordLinked(user)
                  ? t("nav.discord")
                  : t("nav.linkDiscord")}
              </button>
            </div>
          </div>
        )}

        {/* Simple background decoration */}
        {!isTop && (
          <div className="absolute inset-0 -z-10 opacity-5 dark:opacity-10">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary-500 dark:bg-primary-400/60 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-primary-300 dark:bg-primary-300/40 rounded-full blur-3xl"></div>
          </div>
        )}
      </div>

      {/* Discord Link Modal */}
      {showDiscordModal && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-900 rounded-xl max-w-md w-full p-6 shadow-2xl border border-transparent dark:border-dark-700/60">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <DiscordIcon size={24} className="text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-100">
                  {t("settings.linkDiscord")}
                </h2>
              </div>
              <button
                onPointerDown={() => setShowDiscordModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-dark-500 dark:hover:text-dark-300 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-400/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-300 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    {t("settings.discordNotLinked")}
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-200/90">
                    {t("settings.discordNotLinkedDescription")}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onPointerDown={() => setShowDiscordModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onPointerDown={handleLinkDiscord}
                  disabled={isLinkingDiscord}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                    isLinkingDiscord
                      ? "bg-gray-100 text-gray-400 dark:bg-dark-800 dark:text-dark-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
                  }`}
                >
                  {isLinkingDiscord ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      {t("settings.linking")}
                    </>
                  ) : (
                    <>
                      <DiscordIcon size={16} />
                      {t("settings.linkDiscord")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmptyState;
